import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, UserCheck, Rocket, Star, BadgeCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const stepIcons = [ClipboardList, UserCheck, Rocket];
const colorMap = { blue: 'text-blue-400', green: 'text-emerald-400', yellow: 'text-amber-400' };
const stepColors = ['blue', 'green', 'yellow'];

const creatorProfiles = [
  { name: 'Lena Zhang', location: 'New York, NY', handle: '@lena.z', specialty: '生活方式 / 电商' },
  { name: 'Kevin Li', location: 'Los Angeles, CA', handle: '@kevin.li', specialty: '科技 / 应用评测' },
  { name: 'Mia Chen', location: 'San Francisco, CA', handle: '@mia.chen', specialty: '美妆 / 生活分享' },
  { name: 'Eric Wang', location: 'Seattle, WA', handle: '@ericw', specialty: '教育 / 职场' },
  { name: 'Sophie Liu', location: 'Boston, MA', handle: '@sophie.liu', specialty: '旅行 / 城市探索' },
  { name: 'Jason Xu', location: 'Austin, TX', handle: '@jasonxu', specialty: '健身 / 健康' }
];

const Creators = () => {
  const { t } = useLanguage();
  usePageTitle('Creators');
  const steps = (t('creators.steps') || []).map((step, i) => ({ ...step, icon: stepIcons[i], color: stepColors[i] }));
  return (
    <div className="-mt-16">
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-teal-600/12 via-cyan-600/8 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-cyan-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center px-3.5 py-1.5 text-[11px] font-semibold tracking-wide uppercase text-teal-400 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
            {t('creators.title')}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gradient-hero mb-5">
            {t('creators.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl">
            {t('creators.heroDesc')}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="px-7 py-3.5 text-sm font-semibold rounded-full bg-white text-dark hover:shadow-glow-white transition-all duration-400">
              {t('creators.publishRequest')}
            </Link>
            <Link to="/careers" className="px-7 py-3.5 text-sm font-medium rounded-full border border-white/[0.12] text-gray-300 hover:bg-white/[0.06] hover:border-white/25 hover:text-white transition-all duration-400">
              {t('creators.becomeCreator')}
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-10">{t('creators.flowTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="bg-dark-card border border-dark-border rounded-2xl p-6 glow-card hover:border-dark-border-light transition-all duration-400">
                  <div className="w-11 h-11 rounded-xl bg-dark-surface border border-dark-border flex items-center justify-center">
                    <Icon className={`h-5 w-5 ${colorMap[step.color] || 'text-accent'}`} />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-white">{step.title}</h3>
                  <p className="mt-2 text-sm text-gray-400">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Creator Profiles */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white">{t('creators.featuredTitle')}</h2>
            <span className="text-sm text-gray-500">{t('creators.featuredSubtitle')}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {creatorProfiles.map((creator) => (
              <div key={creator.handle} className="bg-dark-card border border-dark-border rounded-2xl p-5 glow-card hover:border-dark-border-light transition-all duration-400">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-white font-bold text-sm">
                    {creator.name.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                  </div>
                  <div><div className="font-semibold text-white text-sm">{creator.name}</div><div className="text-xs text-gray-500">{creator.location}</div></div>
                </div>
                <div className="text-sm text-gray-400 mb-2">{creator.handle}</div>
                <div className="text-[11px] bg-dark-surface text-gray-400 inline-flex px-2.5 py-1 rounded-lg border border-dark-border">
                  {creator.specialty}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-teal-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-gradient bg-dark-card rounded-2xl p-8 md:p-10 shadow-dark-card">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <BadgeCheck className="h-4 w-4 text-teal-400" />{t('creators.founderBadge')}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{t('creators.founderTitle')}</h3>
                <p className="text-sm text-gray-400">{t('creators.founderDesc')}</p>
              </div>
              <div className="bg-dark-surface border border-dark-border rounded-xl p-6 min-w-[220px]">
                <div className="text-sm text-gray-500">{t('creators.creatorScore')}</div>
                <div className="mt-2 flex items-center gap-2 text-white">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <span className="text-2xl font-bold">4.9</span>
                </div>
                <div className="mt-3 text-xs text-gray-500">{t('creators.creatorScoreDesc')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-10">{t('creators.pricingTitle')}</h2>
          <div className="border-gradient bg-dark-card rounded-2xl p-8 md:p-10 max-w-3xl shadow-dark-card">
            <div className="text-sm text-gray-500 mb-2">{t('creators.pricingCustom')}</div>
            <h3 className="text-2xl font-bold text-white mb-4">{t('creators.pricingFlexible')}</h3>
            <ul className="space-y-2.5 text-sm text-gray-400">
              {[t('creators.pricingItem1'), t('creators.pricingItem2'), t('creators.pricingItem3')].map((item, i) => (
                <li key={i} className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span>{item}</li>
              ))}
            </ul>
            <div className="mt-6">
              <Link to="/contact" className="inline-flex px-7 py-3.5 text-sm font-semibold rounded-full bg-white text-dark hover:shadow-glow-white transition-all duration-400">
                {t('creators.getPlan')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Creators;
